import React, { useState } from 'react';
import { Row, Col, Card, Typography, Button, Form, Input, Select, Checkbox, Radio, message } from 'antd';
import { ArrowLeftOutlined, UserOutlined, MailOutlined, PhoneOutlined, CheckCircleFilled } from '@ant-design/icons';
import { PageContainer, ContentContainer } from './styled';
import styled from 'styled-components';
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Helper function to format duration (accepts hours from backend)
const formatDuration = (hours) => {
  if (!hours) return '';
  const wholeHours = Math.floor(hours);
  const remainingMinutes = Math.round((hours - wholeHours) * 60);
  if (wholeHours >= 1 && remainingMinutes === 0) {
    return `${wholeHours} ${wholeHours === 1 ? 'hour' : 'hours'}`;
  }
  if (wholeHours >= 1) {
    return `${wholeHours} ${wholeHours === 1 ? 'hour' : 'hours'} ${remainingMinutes} min`;
  }
  return `${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}`;
};

// Progress bar styled components
const ProgressSteps = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const ProgressStep = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:not(:last-child)::after {
    content: '';
    width: 40px;
    height: 2px;
    background: ${props => {
      if (props.completed && props.nextCompleted) return '#000000';
      if (props.completed && props.nextActive) return '#1890ff';
      return '#e8e8e8';
    }};
    margin-left: 8px;
    
    @media (max-width: 480px) {
      width: 20px;
    }
  }
`;

const StepCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.active ? '#1890ff' : props.completed ? '#000000' : '#f0f0f0'};
  color: ${props => props.active || props.completed ? 'white' : '#999'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
  transition: all 0.3s ease;
  
  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
`;

const StepLabel = styled.span`
  font-size: 14px;
  color: ${props => props.active ? '#262626' : '#999'};
  font-weight: ${props => props.active ? '600' : '400'};
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const BackLink = styled.span`
  color: #1890ff;
  cursor: pointer;
  font-size: 15px;
  text-decoration: underline;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    color: #40a9ff;
    text-decoration: underline;
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const CheckboxWrapper = styled.div`
  .ant-checkbox-wrapper {
    display: flex;
    align-items: flex-start;
    
    .ant-checkbox {
      margin-top: 0.25em;
    }
  }
`;

const UserInfoForm = ({ eventType, timeSlot, onSubmit, onBack }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      // Extract BHA form fields
      const {
        firstName,
        lastName,
        email,
        phone,
        businessEventName,
        pointOfContactName,
        pointOfContactInfo,
        address1,
        address2,
        city,
        state,
        zipCode,
        eventStartTime,
        screeningStartTime,
        indoorOutdoorEvent,
        anticipatedParticipants,
        volunteersAvailable,
        specialInstructions,
        termsAccepted
      } = values;

      // Process form data
      const userInfo = {
        firstName,
        lastName,
        email,
        phone,
        // Store all BHA form data in customData
        businessEventName,
        pointOfContactName,
        pointOfContactInfo,
        address1,
        address2,
        city,
        state,
        zipCode,
        eventStartTime,
        screeningStartTime,
        indoorOutdoorEvent,
        anticipatedParticipants,
        volunteersAvailable,
        specialInstructions,
        termsAccepted
      };

      onSubmit(userInfo);
    } catch (error) {
      console.error('Error submitting form:', error);
      message.error('Failed to submit information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <ContentContainer>
        <div style={{ padding: '40px 0' }} className="user-info-form-wrapper">
          <style>
            {`
              @media (max-width: 768px) {
                .user-info-form-wrapper {
                  padding: 24px 0 !important;
                }
              }
              @media (max-width: 480px) {
                .user-info-form-wrapper {
                  padding: 16px 0 !important;
                }
                .user-info-form-wrapper .ant-card {
                  border-radius: 8px !important;
                }
                .user-info-form-wrapper .ant-card-head {
                  padding: 12px 16px !important;
                }
                .user-info-form-wrapper .ant-card-body {
                  padding: 16px !important;
                }
                .user-info-form-wrapper .ant-form-item {
                  margin-bottom: 16px !important;
                }
              }
            `}
          </style>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <BackLink onClick={onBack}>
                <ArrowLeftOutlined style={{ fontSize: '12px' }} />
                Back to Date & Time
              </BackLink>
              
              {/* Progress Steps */}
              <ProgressSteps>
                <ProgressStep completed nextCompleted>
                  <StepCircle completed>
                    <CheckCircleFilled />
                  </StepCircle>
                  <StepLabel>Select Service</StepLabel>
                </ProgressStep>
                
                <ProgressStep completed nextActive>
                  <StepCircle completed>
                    <CheckCircleFilled />
                  </StepCircle>
                  <StepLabel>Date & Time</StepLabel>
                </ProgressStep>
                
                <ProgressStep>
                  <StepCircle active>3</StepCircle>
                  <StepLabel active>Your Information</StepLabel>
                </ProgressStep>
                
                <ProgressStep>
                  <StepCircle>4</StepCircle>
                  <StepLabel>Confirmation</StepLabel>
                </ProgressStep>
              </ProgressSteps>
              
              <div style={{ marginBottom: '32px' }} className="form-header">
                <style>
                  {`
                    @media (max-width: 768px) {
                      .form-header {
                        margin-bottom: 24px !important;
                      }
                      .form-header h2 {
                        font-size: 24px !important;
                      }
                    }
                    @media (max-width: 480px) {
                      .form-header {
                        margin-bottom: 16px !important;
                      }
                      .form-header h2 {
                        font-size: 20px !important;
                      }
                    }
                  `}
                </style>
                <Title level={2} style={{ margin: '0 0 16px 0', fontSize: '28px', fontWeight: '600', lineHeight: '1.2' }}>
                  Your Information
                </Title>
                
                <Text type="secondary">
                  Please provide your information to complete the booking
                </Text>
              </div>
            </Col>
          </Row>

        <Row gutter={[24, 24]}>
          <Col xs={{ span: 24, order: 2 }} lg={{ span: 16, order: 1 }}>
            <Card 
              title="Personal Information" 
              style={{ 
                marginBottom: '24px',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)'
              }}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                size="large"
                autoComplete="off"
              >
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="firstName"
                      label="First Name"
                      rules={[
                        { required: true, message: 'First name is required' },
                        { max: 40, message: 'First name is too long' },
                        { pattern: /^[\p{L} .'-]+$/u, message: 'Letters only' }
                      ]}
                    >
                      <Input 
                        maxLength={40}
                        prefix={<UserOutlined />} 
                        placeholder="Enter your first name" 
                      />
                    </Form.Item>
                  </Col>
                  
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="lastName"
                      label="Last Name"
                      rules={[
                        { required: true, message: 'Last name is required' },
                        { max: 40, message: 'Last name is too long' },
                        { pattern: /^[\p{L} .'-]+$/u, message: 'Letters only' }
                      ]}
                    >
                      <Input 
                        maxLength={40}
                        prefix={<UserOutlined />} 
                        placeholder="Enter your last name" 
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    { required: true, message: 'Email is required' },
                    { type: 'email', message: 'Please enter a valid email address' },
                    { max: 254, message: 'Email is too long' }
                  ]}
                >
                  <Input 
                    maxLength={254}
                    prefix={<MailOutlined />} 
                    placeholder="Enter your email address" 
                  />
                </Form.Item>

                <Form.Item
                  name="phone"
                  label="Phone Number"
                  rules={[
                    { required: true, message: 'Phone number is required' },
                    {
                      validator: (_, value) => {
                        const digits = (value || '').replace(/\D/g, '');
                        return digits.length >= 10 && digits.length <= 15
                          ? Promise.resolve()
                          : Promise.reject(new Error('Enter a valid phone number'));
                      }
                    }
                  ]}
                >
                  <Input 
                    maxLength={20}
                    prefix={<PhoneOutlined />} 
                    placeholder="Enter your phone number" 
                  />
                </Form.Item>

                {/* Black Heart Association Additional Information */}
                <div className="bha-section" style={{ 
                  marginTop: '32px', 
                  padding: '24px', 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  <style>
                    {`
                      @media (max-width: 480px) {
                        .bha-section {
                          padding: 16px !important;
                          margin-top: 24px !important;
                        }
                      }
                    `}
                  </style>
                  <Title level={4} style={{ marginBottom: '16px', color: '#000000', textAlign: 'left' }}>
                    Black Heart Association
                  </Title>
                  <Text style={{ 
                    display: 'block', 
                    marginBottom: '24px', 
                    fontSize: '14px', 
                    color: '#6c757d',
                    textAlign: 'left',
                    fontStyle: 'italic'
                  }}>
                    Please complete this form in its entirety. Failure to provide all requested information will result in the cancellation of your scheduled appointment.
                  </Text>

                  <Form.Item
                    name="businessEventName"
                    label="Business/Event Name"
                    rules={[
                      { required: true, message: 'Business/Event Name is required' },
                      { max: 100, message: 'Name is too long' }
                    ]}
                  >
                    <Input 
                      size="large" 
                      maxLength={100}
                      placeholder="Enter business or event name"
                      style={{ height: '52px' }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="pointOfContactName"
                    label="Point of Contact Name"
                    rules={[
                      { required: true, message: 'Point of Contact Name is required' },
                      { max: 60, message: 'Name is too long' },
                      { pattern: /^[\p{L} .'-]+$/u, message: 'Letters only' }
                    ]}
                  >
                    <Input 
                      size="large" 
                      maxLength={60}
                      placeholder="Enter point of contact name"
                      style={{ height: '52px' }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="pointOfContactInfo"
                    label="Point of Contact Phone / Email Information"
                    rules={[
                      { required: true, message: 'Point of Contact information is required' },
                      { max: 100, message: 'This is too long' }
                    ]}
                  >
                    <Input 
                      size="large" 
                      maxLength={100}
                      placeholder="Enter phone number or email"
                      style={{ height: '52px' }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="address1"
                    label="Address 1"
                    rules={[
                      { required: true, message: 'Address is required' },
                      { max: 100, message: 'Address is too long' }
                    ]}
                  >
                    <Input 
                      size="large"
                      maxLength={100}
                      placeholder="Enter street address"
                      style={{ height: '52px' }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="address2"
                    label="Address 2"
                    rules={[{ max: 100, message: 'Address is too long' }]}
                  >
                    <Input 
                      size="large"
                      maxLength={100}
                      placeholder="Apartment, suite, etc. (optional)"
                      style={{ height: '52px' }}
                    />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        name="city"
                        label="City"
                        rules={[
                          { required: true, message: 'City is required' },
                          { max: 50, message: 'City is too long' },
                          { pattern: /^[\p{L} .'-]+$/u, message: 'Letters only' }
                        ]}
                      >
                        <Input 
                          size="large"
                          maxLength={50}
                          placeholder="Enter city"
                          style={{ height: '52px' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        name="state"
                        label="State"
                        rules={[
                          { required: true, message: 'State is required' },
                          { max: 30, message: 'State is too long' },
                          { pattern: /^[\p{L} .'-]+$/u, message: 'Letters only' }
                        ]}
                      >
                        <Input 
                          size="large"
                          maxLength={30}
                          placeholder="Enter state"
                          style={{ height: '52px' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        name="zipCode"
                        label="Zip Code"
                        rules={[
                          { required: true, message: 'Zip code is required' },
                          { pattern: /^\d{5}(-\d{4})?$/, message: 'Enter a valid ZIP code' }
                        ]}
                      >
                        <Input 
                          size="large"
                          maxLength={10}
                          placeholder="Enter zip code"
                          style={{ height: '52px' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="eventStartTime"
                    label="Event Start Time"
                    rules={[
                      { required: true, message: 'Event start time is required' },
                      { max: 20, message: 'Too long' }
                    ]}
                  >
                    <Input 
                      size="large" 
                      maxLength={20}
                      placeholder="Enter event start time (e.g., 9:00 AM)"
                      style={{ height: '52px' }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="screeningStartTime"
                    label="Screening Start Time"
                    rules={[
                      { required: true, message: 'Screening start time is required' },
                      { max: 20, message: 'Too long' }
                    ]}
                  >
                    <Input 
                      size="large" 
                      maxLength={20}
                      placeholder="Enter screening start time (e.g., 10:00 AM)"
                      style={{ height: '52px' }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="indoorOutdoorEvent"
                    label="Indoor or Outdoor Event?"
                    rules={[{ required: true, message: 'Please select indoor or outdoor event' }]}
                  >
                    <Select 
                      size="large" 
                      placeholder="Select event type"
                      style={{ height: '52px' }}
                    >
                      <Option value="indoor">Indoor</Option>
                      <Option value="outdoor">Outdoor</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="anticipatedParticipants"
                    label="Anticipated Number of Participants"
                    rules={[
                      { required: true, message: 'Number of participants is required' },
                      { pattern: /^\d{1,6}$/, message: 'Enter a valid number' }
                    ]}
                  >
                    <Input 
                      size="large" 
                      type="number" 
                      min={1}
                      max={999999}
                      maxLength={6}
                      placeholder="Enter expected number of participants"
                      style={{ height: '52px' }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="volunteersAvailable"
                    label="Will volunteers be available?"
                    rules={[{ required: true, message: 'Please select if volunteers will be available' }]}
                  >
                    <Radio.Group>
                      <Radio value="yes">Yes</Radio>
                      <Radio value="no">No</Radio>
                    </Radio.Group>
                  </Form.Item>

                  <Form.Item
                    name="majorityEnglishSpeaking"
                    label="Majority English speaking participants?"
                    rules={[{ required: true, message: 'Please select language preference' }]}
                  >
                    <Radio.Group>
                      <Radio value="yes">Yes</Radio>
                      <Radio value="no">No</Radio>
                    </Radio.Group>
                  </Form.Item>

                  <Form.Item
                    name="specialInstructions"
                    label="Please include any special event instructions!"
                    rules={[{ max: 500, message: 'Please keep this under 500 characters' }]}
                  >
                    <TextArea 
                      size="large"
                      rows={6}
                      maxLength={500}
                      showCount
                      placeholder="Enter any special instructions, accessibility needs, or other important information"
                      style={{ minHeight: '120px' }}
                    />
                  </Form.Item>
                </div>

                {/* Terms and conditions */}
                <Form.Item
                  name="agreeToTerms"
                  valuePropName="checked"
                  rules={[{ 
                    validator: (_, value) => 
                      value ? Promise.resolve() : Promise.reject(new Error('You must agree to the terms')) 
                  }]}
                  style={{ marginTop: '32px' }}
                >
                  <CheckboxWrapper>
                    <Checkbox>
                      I agree to the terms and conditions and consent to sharing my information 
                      for this health screening appointment.
                    </Checkbox>
                  </CheckboxWrapper>
                </Form.Item>

                <Form.Item style={{ marginTop: '32px', marginBottom: 0 }} className="submit-button-wrapper">
                  <style>
                    {`
                      @media (max-width: 480px) {
                        .submit-button-wrapper .ant-btn {
                          width: 100% !important;
                          min-width: unset !important;
                        }
                      }
                    `}
                  </style>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    size="large"
                    loading={loading}
                    style={{ minWidth: '200px' }}
                  >
                    Confirm Booking
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>

          <Col xs={{ span: 24, order: 1 }} lg={{ span: 8, order: 2 }}>
            <Card 
              title="Booking Summary"
              style={{ 
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)'
              }}
            >
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Service:</Text>
                <div style={{ marginTop: '4px' }}>{eventType.name}</div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <Text strong>Date:</Text>
                <div style={{ marginTop: '4px' }}>
                  {moment(timeSlot.date).format('dddd, MMMM D, YYYY')}
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <Text strong>Time:</Text>
                <div style={{ marginTop: '4px' }}>
                  {moment(timeSlot.startTime).format('h:mm A')} - {moment(timeSlot.startTime).add(eventType.duration, 'minutes').format('h:mm A')} CST
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <Text strong>Duration:</Text>
                <div style={{ marginTop: '4px' }}>{formatDuration(eventType.duration)}</div>
              </div>

              {eventType.description && (
                <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    {eventType.description}
                  </Text>
                </div>
              )}
            </Card>
          </Col>
        </Row>
        </div>
      </ContentContainer>
    </PageContainer>
  );
};

export default UserInfoForm;
